import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { fetchAllPaginated } from "@/lib/supabase-paginate";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { osloWeekBounds } from "@/lib/follow-up-calc";
import type { DBFollowUp } from "@/lib/db-types";

/**
 * Oppfølginger (CRM-2.2), spørringer. Mutasjonene ligger i use-follow-up-mutations.ts.
 * Query-nøklene har eier/kunde som 2. element (CACHE_REGISTRY).
 */

export interface FollowUpWithRelations extends DBFollowUp {
  customer: { id: string; name: string; contact_phone: string | null } | null;
  contact: { id: string; full_name: string; title: string | null; email: string | null; phone: string | null } | null;
  opportunity: { id: string; title: string } | null;
  owner?: { id: string; full_name: string } | null;
}

const SELECT_WITH_RELATIONS =
  "*, customer:customers(id, name, contact_phone), contact:customer_contacts(id, full_name, title, email, phone), opportunity:opportunities(id, title), owner:users!follow_ups_owner_id_fkey(id, full_name)";

/**
 * Min liste for en uke: åpne oppfølginger med forfall senest søndag (forfalte
 * medregnet) pluss de jeg har fullført i uka. Alt uten uke-slutt-grense for
 * lukkede ville vokst ubegrenset, derfor begrenses de av ukestart.
 */
export function useMyFollowUps({ weekOf }: { weekOf?: Date } = {}) {
  const { activeUser } = useImpersonation();
  const ownerId = activeUser?.id;
  const week = osloWeekBounds(weekOf ?? new Date());
  return useQuery({
    queryKey: ["follow-ups", ownerId, { weekStart: week.startDate }],
    queryFn: async (): Promise<FollowUpWithRelations[]> =>
      fetchAllPaginated<FollowUpWithRelations>(
        "follow_ups",
        SELECT_WITH_RELATIONS,
        (q) =>
          q
            .eq("owner_id", ownerId!)
            .or(
              `and(status.eq.open,due_date.lte.${week.endDate}),and(status.eq.done,closed_at.gte.${week.start.toISOString()})`,
            ),
      ),
    enabled: !!ownerId,
    staleTime: 60 * 1000,
  });
}

/** Kundens åpne oppfølginger. Bundet på kunde. */
export function useCustomerFollowUps(customerId: string | undefined) {
  return useQuery({
    queryKey: ["customer-follow-ups", customerId],
    queryFn: async (): Promise<FollowUpWithRelations[]> => {
      const { data, error } = await supabase
        .from("follow_ups")
        .select(SELECT_WITH_RELATIONS)
        .eq("customer_id", customerId!)
        .eq("status", "open")
        .order("due_date", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as FollowUpWithRelations[];
    },
    enabled: !!customerId,
    staleTime: 60 * 1000,
  });
}

/**
 * Én oppfølging med relasjoner. Signalkortet bruker den til å gå rett fra
 * «ta ansvar» til e-post, samtale eller utsett på oppfølgingen claim_signal lagde.
 */
export async function fetchFollowUpWithRelations(id: string): Promise<FollowUpWithRelations> {
  const { data, error } = await supabase.from("follow_ups").select(SELECT_WITH_RELATIONS).eq("id", id).single();
  if (error) throw error;
  return data as unknown as FollowUpWithRelations;
}

export interface NextDue {
  date: string;
  count: number;
}

/** Tidligste forfall etter denne uka (til «Neste forfall» når køen er tom). */
export function useNextFollowUpDue() {
  const { activeUser } = useImpersonation();
  const ownerId = activeUser?.id;
  const week = osloWeekBounds(new Date());
  return useQuery({
    queryKey: ["follow-ups", ownerId, "next-due", week.startDate],
    queryFn: async (): Promise<NextDue | null> => {
      const { data, error } = await supabase
        .from("follow_ups")
        .select("due_date")
        .eq("owner_id", ownerId!)
        .eq("status", "open")
        .gt("due_date", week.endDate)
        .order("due_date", { ascending: true })
        .limit(100);
      if (error) throw error;
      const dates = (data ?? []).map((r) => r.due_date as string);
      if (dates.length === 0) return null;
      return { date: dates[0], count: dates.filter((d) => d === dates[0]).length };
    },
    enabled: !!ownerId,
    staleTime: 60 * 1000,
  });
}
